import { Component, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from "@angular/core";
import './v-progress-bar.component.scss';

export enum ProgressColourPalette {
    Greyscale,
    GreenOnly,
    RAG
}

export interface IProgressBarColourRules {
    GreenZoneStart: number,
    YellowZoneStart: number
}

const defaultColourRules: IProgressBarColourRules = {
    GreenZoneStart: 50,
    YellowZoneStart: 25
};

@Component({
    selector: 'v-progress-bar',
    template: require('./v-progress-bar.component.html')
})
export class VProgressBar implements OnChanges {

    @Input() percentage: number;
    @Input() total: number;
    @Input() count: number;
    @Input() subject: string;

    @Input() roundNumber: boolean = true;
    @Input() showLabel = true;
    @Input() grayscale: boolean;
    @Input() palette = ProgressColourPalette.GreenOnly;
    @Input() colourRules: IProgressBarColourRules = defaultColourRules;

    // ---

    classesAdded: string[];
    roundTo: string = '1.0-1';

    constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    }

    ngOnInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes["percentage"] || changes["count"])  {
            this.render();
        }

        if (changes["grayscale"]) {
            this.updateClasses();
        }

        if (changes["roundNumber"]) {
            this.setRounding();
        }
    }

    get showPercentage(): boolean {
        return (this.percentage || this.percentage == 0) && (!this.count && this.count !== 0);
    }

    // ---

    private render(): void {
        if ((this.count || this.count === 0) && (this.total || this.total === 0)) {
            this.percentage = this.count / this.total * 100;
        }
        if (!this.percentage) {
            return;
        }

        this.validateProgress();
        this.updateProgressBar();
        this.updateClasses();

    }

    private validateProgress() {

        if (isNaN(this.percentage) || typeof this.percentage != "number") {
            this.percentage = 0;
        }

        if (this.percentage < 0) {
            this.percentage = 0;
        }

        if (this.percentage > 100) {
            this.percentage = 100;
        }
    }

    private updateProgressBar() {
        const htmlElement = this.elementRef.nativeElement as HTMLElement;
        htmlElement.style.setProperty("--progress", this.percentage + "%");
    }

    private updateClasses() {

        this.classesAdded?.forEach(classAdded => this.renderer.removeClass(this.elementRef.nativeElement, classAdded));

        this.classesAdded = [this.getClassNameForPercentage()];

        if (this.grayscale) {
            this.classesAdded.push("grayscale");
        }

        this.classesAdded.forEach(classAdded => this.renderer.addClass(this.elementRef.nativeElement, classAdded));
    }

    private getClassNameForPercentage(): string {
        let className = "";

        switch (this.palette) {
            case ProgressColourPalette.GreenOnly:
                className += "ok";
                break;

            case ProgressColourPalette.RAG:
                className += this.getRAGClass();
                break;

            default:
                break;
        }

        return className;
    }

    private getRAGClass(): string {

        switch (true) {
            case this.percentage >= this.colourRules.GreenZoneStart:
                return "ok";
            case this.percentage >= this.colourRules.YellowZoneStart:
                return "warning";
            default:
                return "error";
        }
    }

    private setRounding(): void {
        this.roundTo = this.roundNumber ? '1.0-0' : '1.0-1';
    }
}
